import { type FC } from 'react'

import { type ProspectDto } from '@api'
import { FormProvider } from 'react-hook-form'

import InfoIcon from '@/assets/icons/icon-info.svg'
import { CountrySelect } from '@/components/ui/CountrySelect/SelectCountry.tsx'
import { Button } from '@/components/ui/button.tsx'
import { Dialog } from '@/components/ui/dialog'
import { FormCheckbox } from '@/components/ui/form/form-checkbox.tsx'
import { FormField } from '@/components/ui/form/form-field.tsx'
import { Tooltip } from '@/components/ui/tooltip'
import { useProspectForm } from '@/dialogs/AddProspectDialog/useProspectForm.ts'
import type { DialogProps } from '@/dialogs/types'

import { ALLOWED_LINK_PLATFORMS } from './constants'

interface Props extends DialogProps {
  activeProspect?: ProspectDto
}

export const AddProspectDialog: FC<Props> = ({
  activeProspect,
  isOpen,
  onClose,
}) => {
  const { onSubmit, form, isLoading } = useProspectForm({
    onClose,
    activeProspect,
  })

  const handleCountrySelect = (value: string | null) => {
    form.setValue('country', value || '', { shouldValidate: true })
  }

  return (
    <Dialog
      show={isOpen}
      className="w-[312px]"
      testId="add-prospect-modal"
    >
      <Dialog.Header className="mb-6">
        <Dialog.Title className="text-center">
          {activeProspect ? 'Edit' : 'Add'} Prospect
        </Dialog.Title>
      </Dialog.Header>
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid gap-6">
            <FormField
              name="name"
              label="Name"
              placeholder="Enter Prospect Name"
              testId="prospect-name"
            />
            <FormField
              name="country"
              label="Country"
            >
              <CountrySelect
                value={form.watch('country')}
                onChange={handleCountrySelect}
                testId="prospect-country"
              />
            </FormField>
            <FormField
              name="website"
              label="Website"
              placeholder="Enter Website URL"
              disabled={!!activeProspect}
              suffix={
                !activeProspect && (
                  <Tooltip
                    content={`Supported websites:\n${ALLOWED_LINK_PLATFORMS.join('\n')}`}
                    contentClassName="whitespace-pre-wrap text-left"
                  >
                    <InfoIcon className="text-primary-500 h-4 w-4 cursor-pointer" />
                  </Tooltip>
                )
              }
              testId="prospect-website"
            />
            <FormCheckbox
              name="onlyFansUser"
              label="OnlyFans User"
              className="flex items-center"
              testId="prospect-onlyfans-user"
            />
          </div>
          <Dialog.Footer>
            <Button
              className="w-full"
              variant="outline"
              onClick={onClose}
              type="button"
            >
              Cancel
            </Button>
            <Button
              className="w-full"
              isLoading={isLoading}
              type="submit"
              testId="prospect-submit-button"
            >
              {activeProspect ? 'Save' : 'Add'} Prospect
            </Button>
          </Dialog.Footer>
        </form>
      </FormProvider>
    </Dialog>
  )
}
